import { GridColDef } from "@mui/x-data-grid-pro";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DataQualityCell } from "../components/DataQualityCell";
import { ConsumptionRow } from "../models/consumptionRow";
import { energyDataStatusOrder } from "../models/energyDataQuality";
import { decimalFormatter } from "../utilities/decimalFormatter";
import { statusLabel } from "../utilities/statusLabel";
import { formatDate } from "@/hooks/metrics/useFormattedDate";

/**
 * Columns not worth the width by default. Every one of them is a detail behind a figure that is
 * already summarised elsewhere on the row, so they are there for a reader digging into a
 * particular vehicle rather than for the overview.
 */
export const CONSUMPTION_HIDDEN_COLUMNS = {
  measuredDistanceKm: false,
  measuredFrom: false,
  measuredTo: false,
  fuelSource: false,
};

/**
 * The columns of the consumption view.
 *
 * Units belong in the header and never in the cell: a cell holding "8,2 l/100 km" is text, and a
 * column of text cannot be summed, sorted numerically or charted after the export. Declaring the
 * column as a number is also what makes the Excel export write a real number into it.
 *
 * @param vinDecodedByDeviceId - Make and model per vehicle, from the decoded VIN.
 * @returns The column definitions.
 */
export function useConsumptionColumns(
  vinDecodedByDeviceId: Map<string, string>,
): GridColDef<ConsumptionRow>[] {
  const { t, i18n } = useTranslation();
  const language = i18n.language;

  // Memoised because DataGridPro rebuilds its whole column state on a new array identity, and
  // this page re-renders once per arriving vehicle — hundreds of times while a fleet is read.
  return useMemo<GridColDef<ConsumptionRow>[]>(() => {
    const decimals = decimalFormatter(language);

    return [
    {
      field: "status",
      headerName: t("energyReport.columns.quality"),
      minWidth: 180,
      // Sorted by severity rather than by the label, so the vehicles worth looking at come first.
      sortComparator: (first, second) =>
        energyDataStatusOrder(first as ConsumptionRow["status"]) -
        energyDataStatusOrder(second as ConsumptionRow["status"]),
      renderCell: ({ row }) => <DataQualityCell row={row} />,
      valueFormatter: (value: ConsumptionRow["status"]) => statusLabel(value, t),
    },
    {
      field: "name",
      headerName: t("energyReport.columns.asset"),
      minWidth: 200,
      flex: 1.2,
    },
    {
      field: "licensePlate",
      headerName: t("energyReport.columns.licensePlate"),
      minWidth: 130,
    },
    {
      field: "vinDecoded",
      headerName: t("energyReport.columns.vinDecoded"),
      minWidth: 180,
      flex: 1,
      sortable: false,
      valueGetter: (_value, row) => vinDecodedByDeviceId.get(row.id) ?? "",
    },
    {
      field: "fuelRatePer100Km",
      headerName: t("energyReport.columns.fuelRate"),
      description: t(
        "energyReport.columns.rateHint",
        "Bezogen auf die Strecke innerhalb des Zeitraums, für den Zählerstände vorliegen.",
      ),
      type: "number",
      minWidth: 160,
      valueFormatter: value => decimals(value as number | null),
    },
    {
      field: "electricRatePer100Km",
      headerName: t("energyReport.columns.electricRate"),
      description: t(
        "energyReport.columns.rateHint",
        "Bezogen auf die Strecke innerhalb des Zeitraums, für den Zählerstände vorliegen.",
      ),
      type: "number",
      minWidth: 160,
      valueFormatter: value => decimals(value as number | null),
    },
    {
      field: "fuelUsedLiters",
      headerName: t("energyReport.columns.fuelUsed"),
      type: "number",
      minWidth: 180,
      valueFormatter: value => decimals(value as number | null),
    },
    {
      field: "electricUsedKWh",
      headerName: t("energyReport.columns.electricUsed"),
      type: "number",
      minWidth: 180,
      valueFormatter: value => decimals(value as number | null),
    },
    {
      field: "filledLiters",
      headerName: t("energyReport.columns.filled"),
      type: "number",
      minWidth: 130,
      valueFormatter: value => decimals(value as number | null),
    },
    {
      field: "fuelDeviationLiters",
      headerName: t("energyReport.columns.fuelDeviation"),
      description: t(
        "energyReport.columns.fuelDeviationHint",
        "Getankte Menge minus gemessenem Verbrauch. Ein deutlicher Überschuss deutet auf einen fehlerhaften Messwert oder Kraftstoffverlust hin.",
      ),
      type: "number",
      minWidth: 200,
      valueFormatter: value => decimals(value as number | null),
    },
    {
      field: "chargedKWh",
      headerName: t("energyReport.columns.charged"),
      type: "number",
      minWidth: 150,
      valueFormatter: value => decimals(value as number | null),
    },
    {
      field: "chargeDurationHours",
      headerName: t("energyReport.columns.chargeDuration"),
      type: "number",
      minWidth: 130,
      valueFormatter: value => decimals(value as number | null),
    },
    {
      field: "distanceKm",
      headerName: t("energyReport.columns.distance"),
      type: "number",
      minWidth: 130,
      valueFormatter: value => decimals(value as number | null, 0),
    },
    {
      field: "measuredDistanceKm",
      headerName: t("energyReport.columns.measuredDistance"),
      description: t(
        "energyReport.columns.measuredDistanceHint",
        "Strecke innerhalb des Zeitraums, für den Zählerstände vorliegen. Nur diese steckt in den Verbrauchswerten.",
      ),
      type: "number",
      minWidth: 170,
      valueFormatter: value => decimals(value as number | null, 0),
    },
    {
      field: "measuredFrom",
      type: "dateTime",
      headerName: t("energyReport.columns.measuredFrom"),
      minWidth: 160,
      valueFormatter: value => formatDate(value as Date | undefined, language),
    },
    {
      field: "measuredTo",
      type: "dateTime",
      headerName: t("energyReport.columns.measuredTo"),
      minWidth: 160,
      valueFormatter: value => formatDate(value as Date | undefined, language),
    },
    {
      field: "fuelSource",
      headerName: t("energyReport.columns.fuelSource"),
      minWidth: 190,
      valueGetter: (_value, row) => {
        if (row.fuelSource === "vehicle") return t("energyReport.fuelSource.vehicle");
        if (row.fuelSource === "telematics") {
          return t("energyReport.fuelSource.telematics");
        }
        return "";
      },
    },
  ];
  }, [t, language, vinDecodedByDeviceId]);
}
